import 'leaflet/dist/leaflet.css';
import {
    createCustomIcon,
    DraggableMarker,
    MapController,
    MapReference,
    mapStyles,
    NoCoordinatesView
} from './components';
import { usePropertyMap } from './hooks';
import { PropertyMapProps } from './types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Globe, MapPin, Move, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Circle, MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';

export const PropertyMap = ({
    geoLocation,
    address,
    city,
    state,
    country,
    name,
    isEditing = false,
    onLocationChange
}: PropertyMapProps) => {
    const { t } = useTranslation();

    if (!geoLocation || !geoLocation.coordinates || geoLocation.coordinates.length !== 2) {
        return <NoCoordinatesView />;
    }

    const [longitude, latitude] = geoLocation.coordinates;

    if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
        return null;
    }

    const {
        activeMapStyle,
        mapRef,
        radiusSize,
        handleMarkerDragEnd,
        setMapRef,
        handleMapStyleChange
    } = usePropertyMap({
        latitude,
        longitude,
        onLocationChange
    });

    const locationText = [address, city, state, country]
        .filter(Boolean)
        .join(', ');

    const customIcon = createCustomIcon();

    return (
        <Card className="border border-border shadow-sm hover:shadow-md transition-shadow duration-300">
            <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                    <CardTitle className="text-lg font-medium flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-primary" />
                        {t('properties.location.map')}
                    </CardTitle>
                    <Tabs value={activeMapStyle} onValueChange={handleMapStyleChange}>
                        <TabsList className="h-8">
                            {Object.entries(mapStyles).map(([key, style]) => (
                                <TabsTrigger
                                    key={key}
                                    value={key}
                                    className="text-xs px-2"
                                    disabled={isEditing}
                                >
                                    {t(`properties.location.mapStyles.${key}`)}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>

                <div className="flex items-center justify-between mt-10">
                    {isEditing && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Move className="h-3 w-3" />
                            {t('properties.location.dragMarker')}
                        </p>
                    )}
                </div>
            </CardHeader>

            <CardContent className="p-4 relative">
                <div className="w-full rounded-lg overflow-hidden h-[400px] relative shadow-md border border-border">
                    <MapContainer
                        center={[latitude, longitude] as [number, number]}
                        zoom={14}
                        scrollWheelZoom={true}
                        style={{ height: '100%', width: '100%' }}
                        className="z-0 pointer-events-auto"
                        zoomControl={false}
                        attributionControl={false}
                    >
                        <MapReference setMapRef={setMapRef} />
                        <TileLayer
                            attribution={mapStyles[activeMapStyle].attribution}
                            url={mapStyles[activeMapStyle].url}
                        />
                        {isEditing && onLocationChange ? (
                            <>
                                <DraggableMarker
                                    position={[latitude, longitude]}
                                    icon={customIcon}
                                    onDragEnd={handleMarkerDragEnd}
                                >
                                    <Popup className="leaflet-custom-popup">
                                        <div className="text-sm p-1">
                                            <p className="font-medium">{name || t('properties.location.property')}</p>
                                            {locationText && <p className="text-muted-foreground text-xs">{locationText}</p>}
                                            <Separator className="my-2" />
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <MapPin className="h-3 w-3" />
                                                <span>{latitude.toFixed(6)}, {longitude.toFixed(6)}</span>
                                            </div>
                                            <p className="text-xs mt-1 text-amber-500 font-medium flex items-center gap-1">
                                                <Move className="h-3 w-3" />
                                                {t('properties.location.dragMarkerHint')}
                                            </p>
                                        </div>
                                    </Popup>
                                </DraggableMarker>
                                <Circle
                                    center={[latitude, longitude]}
                                    radius={radiusSize}
                                    pathOptions={{
                                        fillColor: '#10b981',
                                        fillOpacity: 0.2,
                                        color: '#10b981',
                                        weight: 1
                                    }}
                                />
                            </>
                        ) : (
                            <>
                                <Marker
                                    position={[latitude, longitude] as [number, number]}
                                    icon={customIcon}
                                >
                                    <Popup className="leaflet-custom-popup">
                                        <div className="text-sm p-1">
                                            <p className="font-medium">{name || t('properties.location.property')}</p>
                                            {locationText && <p className="text-muted-foreground text-xs">{locationText}</p>}
                                            <Separator className="my-2" />
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <MapPin className="h-3 w-3" />
                                                <span>{t('properties.location.approximateLocation')}</span>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                                <Circle
                                    center={[latitude, longitude]}
                                    radius={radiusSize}
                                    pathOptions={{
                                        fillColor: '#10b981',
                                        fillOpacity: 0.2,
                                        color: '#10b981',
                                        weight: 1
                                    }}
                                />
                            </>
                        )}
                        <MapController markerPosition={[latitude, longitude]} />
                    </MapContainer>
                </div>
            </CardContent>

            <CardFooter className="p-4 flex gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-2"
                    onClick={() => {
                        window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, '_blank');
                    }}
                >
                    <Globe className="h-4 w-4" />
                    {t('properties.location.openInGoogleMaps')}
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-2"
                    onClick={() => {
                        navigator.clipboard.writeText(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`);
                    }}
                >
                    <Copy className="h-4 w-4" />
                    {t('properties.location.copyLink')}
                </Button>
            </CardFooter>
        </Card>
    );
}; 